import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { translate } from 'components/Translation';
import { Switch, Route, useLocation } from 'react-router-dom';
import ManageGroups from 'views/PrepaidLoyalty/ManageGroups';
import RegisteringNewLoyalty from 'views/PrepaidLoyalty/RegisteringNewLoyalty';
import { useHistory } from 'react-router';
import CustomConfirmationDialog, {
	IConfirmationDialogButton,
} from 'components/CustomConfirmationDialog/CustomConfirmationDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { BodyS, BodySBold, HeadingLBold } from 'components/Typography/Typography';
import AddNewServices from 'views/PrepaidLoyalty/AddNewServices';
import AddTopUp from 'views/PrepaidLoyalty/AddTopUp';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { setToast } from 'redux/modules/toast/actions';
import { throwError } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LoyaltyGroupService } from 'services/loyaltyGroups/loyaltyGroups';
import { LoyaltyGroup } from 'utils/api/models/prepaidLoyalty/loyaltyGroup';
import { CreateUpdateLoyaltyResponse } from 'utils/api/models/prepaidLoyalty/createUpdateLoyaltyResponse';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/registerNewLoyaltyStyle';
import invalidateCache from 'utils/api/invalidateCache';
import { URL_PATHS } from './commons/constants';
import PaymentRejectedDialog from './commons/PaymentRejectedDialog';
import PaymentFrame from 'components/PaymentFrame/PaymentFrame';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { ISelectedAccount } from 'views/common/AccountFilterTable';
import { AccountsService } from 'services/accounts/accounts';
import { useDispatch, useSelector } from 'react-redux';
import { loadQualtrics } from '../../utils/qualtrics';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IIndexProps {
	match: {
		path: string;
	};
}

const PrepaidLoyalty = ({ match }: IIndexProps) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
	const [confirmationDialogState, setConfirmationDialogState] = useState<boolean>(false);
	const [newSubscriptionDialogState, setNewSubscriptionDialogState] = useState<{
		open: boolean;
		error: boolean;
		isTopUp: boolean;
		topUpValue: number;
		subscriptionList: string[];
		numbers: string[];
		groupName: string;
	}>({
		open: false,
		error: false,
		isTopUp: true,
		topUpValue: 0,
		subscriptionList: [],
		numbers: [],
		groupName: '',
	});
	const [paymentDialog, setPaymentDialog] = useState<types.topUpAddOns.paymentDialog>();
	const [showPaymentFrame, setShowPaymentFrame] = useState<boolean>(false);
	const [currentGroupResp, setCurrentGroupResp] = useState<CreateUpdateLoyaltyResponse>();
	const [groups, setGroups] = useState<LoyaltyGroup[]>([]);
	const [noGroups, setNoGroups] = useState(false);
	const [accounts, setAccounts] = useState<Array<SingleCorpAccount>>([]);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const history = useHistory();
	const dispatch = useDispatch();
	let location = useLocation();

	const goToPrepaidLoyalty = () => {
		history.push(match.path);
	};

	const hideConfirmationDialog = () => {
		setConfirmationDialogOpen(false);
		if (currentGroupResp) {
			invalidateCache(['getLoyaltyGroups'], true);
			getGroupsFromServer();
		}
	};

	const addGroup = (name: string, numbers: string[]) => {
		setNoGroups(false);
		dispatch(showLoadingIndicator());
		const data = {
			name,
			numbers,
			upId: selectedAccount?.id,
		};

		LoyaltyGroupService.createLoyaltyGroup(data)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setLoading(false);
				})
			)
			.subscribe(
				(loyaltyGroup) => {
					const loyaltyId = loyaltyGroup?.id;
					if (loyaltyId) {
						goToPrepaidLoyalty();
						setCurrentGroupResp(loyaltyGroup);
						setConfirmationDialogState(true);
						setConfirmationDialogOpen(true);
					} else {
						dispatch(
							setToast({
								error: true,
								label: translate('error.generic-title'),
								actionType: translate('error'),
							})
						);
					}
				},
				(error) => {
					throwError(error);
				}
			);
	};

	const addSubscription = (
		groupId: number,
		isTopUp: boolean,
		topUpValue: number,
		subscriptionList: string[],
		numbers: string[]
	) => {
		invalidateCache(['getLoyaltyGroups', 'getLoyaltyGroupDetails'], true);
		const group = groups.find((grp) => grp.id === groupId);
		setNewSubscriptionDialogState({
			open: true,
			isTopUp,
			error: false,
			topUpValue,
			subscriptionList,
			numbers,
			groupName: group ? group.name : '',
		});
		setGroups((oldGroups) =>
			oldGroups.map((singleGroup) =>
				singleGroup.id === groupId
					? {
							...singleGroup,
							subscriptionsCount: singleGroup.subscriptionsCount + 1,
					  }
					: singleGroup
			)
		);
	};

	const addSubscriptionError = () => {
		setNewSubscriptionDialogState({
			open: true,
			isTopUp: true,
			error: true,
			topUpValue: 0,
			subscriptionList: [],
			numbers: [],
			groupName: '',
		});
	};

	const closeNewSubscriptionDialog = () => {
		setNewSubscriptionDialogState((oldState) => {
			return {
				...oldState,
				open: false,
			};
		});
		localStorage.removeItem('pendingPayment');
		loadQualtrics({
			activity_type: `Gift Programme - ${newSubscriptionDialogState.error ? 'Failure' : 'Success'}`,
		});
	};

	const getGroupsFromServer = useCallback(() => {
		dispatch(showLoadingIndicator());
		return LoyaltyGroupService.getLoyaltyGroups(Number(selectedAccount?.id) || null)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe((loyaltyGroupsResp) => {
				setGroups(loyaltyGroupsResp.loyaltyGroups);
				setNoGroups(loyaltyGroupsResp.loyaltyGroups.length === 0);
			});
	}, [selectedAccount, dispatch]);

	useEffect(() => {
		const subscription = AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
			setAccounts(accounts);
		});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		const subscription = getGroupsFromServer();

		return () => {
			subscription.unsubscribe();
		};
	}, [getGroupsFromServer]);

	// setting account to null when the path changes to prevent redirection
	useEffect(() => {
		if (location.pathname === `/${URL_PATHS.PREPAID_LOYALTY_ROOT}`) {
			setSelectedAccount(null);
		}
	}, [location.pathname]);

	const numbersFromSLNumbers = () => {
		return currentGroupResp?.numbers.map((num) => num.number);
	};

	const hidePaymentDialog = () => {
		setPaymentDialog(undefined);
	};

	useEffect(() => {
		if (location.pathname === `/${URL_PATHS.PREPAID_LOYALTY_ROOT}` && noGroups && selectedAccount) {
			history.push(`${match.path}/${URL_PATHS.REGISTER_NEW_LOYALTY}`);
		}
	}, [selectedAccount, noGroups]);

	useEffect(() => {
		const pendingPaymentFromLocalStorage = localStorage.getItem('pendingPayment');
		if (location.search.includes('success') && pendingPaymentFromLocalStorage) {
			const subscription = JSON.parse(pendingPaymentFromLocalStorage);
			setNewSubscriptionDialogState({
				open: true,
				error: false,
				isTopUp: false,
				numbers: subscription.numbers,
				groupName: subscription.groupName,
				topUpValue: 0,
				subscriptionList: subscription.subscriptionList,
			});
		}
	}, []);

	return (
		<>
			<BreadcrumbsItem to={match.path}>{translate('path.prepaid.loyalty')}</BreadcrumbsItem>
			<Switch>
				<Route
					path={match.path}
					exact
					render={(props: any) => (
						<ManageGroups
							{...props}
							groups={groups}
							refreshGroups={getGroupsFromServer}
							accounts={accounts}
							selectedAccount={selectedAccount}
							setSelectedAccount={setSelectedAccount}
						/>
					)}
				/>
				<Route
					path={`${match.path}/${URL_PATHS.REGISTER_NEW_LOYALTY}`}
					render={() => (
						<RegisteringNewLoyalty loading={loading} setLoading={setLoading} addGroup={addGroup} />
					)}
				/>
				<Route
					path={`${match.path}/${URL_PATHS.ADD_NEW_SERVICES}/:encodedGroupId`}
					render={(props: any) => (
						<AddNewServices
							{...props}
							addSubscription={addSubscription}
							addSubscriptionError={addSubscriptionError}
							setPaymentDialog={setPaymentDialog}
							setShowPaymentFrame={setShowPaymentFrame}
						/>
					)}
				/>
				<Route
					path={`${match.path}/${URL_PATHS.ADD_TOP_UP}/:encodedGroupId`}
					render={(props: any) => (
						<AddTopUp
							{...props}
							addSubscription={addSubscription}
							addSubscriptionError={addSubscriptionError}
							setPaymentDialog={setPaymentDialog}
							setShowPaymentFrame={setShowPaymentFrame}
						/>
					)}
				/>
			</Switch>
			<ConditionalRender show={showPaymentFrame} onTrue={() => <PaymentFrame />} />
			<ConditionalRender
				show={paymentDialog === 'paymentRejected'}
				onTrue={() => <PaymentRejectedDialog isOpen={true} hideDialog={hidePaymentDialog} />}
			/>
			<PrepaidConfirmationDialog
				isOpen={confirmationDialogOpen}
				hideDialog={hideConfirmationDialog}
				isError={!confirmationDialogState}
				heading={translate(confirmationDialogState ? 'prepaid.loyalty.success' : 'something.went.wrong')}
				button={
					confirmationDialogState
						? {
								name: translate('yes'),
								color: 'primary-light',
								onClick: hideConfirmationDialog,
						  }
						: undefined
				}
				numbers={numbersFromSLNumbers() || []}
				description={
					<>
						<BodyS disableMargin inline>
							{translate('prepaid.loyalty.register.loyalty.success.description1')}
						</BodyS>
						<BodySBold disableMargin inline>
							{currentGroupResp?.loyaltyGroup?.name || ''}
						</BodySBold>
					</>
				}
			/>
			<PrepaidConfirmationDialog
				isOpen={newSubscriptionDialogState.open}
				hideDialog={closeNewSubscriptionDialog}
				isError={newSubscriptionDialogState.error}
				heading={translate(
					newSubscriptionDialogState.error ? 'something.went.wrong' : 'prepaid.loyalty.success'
				)}
				button={{
					name: translate('okay'),
					color: 'primary-light',
					onClick: closeNewSubscriptionDialog,
				}}
				numbers={newSubscriptionDialogState.numbers}
				description={
					<>
						<BodyS disableMargin inline>
							{newSubscriptionDialogState.isTopUp
								? translate('prepaid.loyalty.register.loyalty.topup.success.description')
								: translate('prepaid.loyalty.register.loyalty.addon.success.description')}
						</BodyS>
						<BodySBold disableMargin inline>
							{newSubscriptionDialogState.isTopUp
								? newSubscriptionDialogState.topUpValue.toFixed(3) + ' OMR'
								: newSubscriptionDialogState.subscriptionList.join(', ')}
						</BodySBold>
						<BodyS disableMargin inline>
							{translate('prepaid.loyalty.register.loyalty.success.on.the')}
						</BodyS>
						<BodySBold disableMargin inline>
							{newSubscriptionDialogState.groupName}
						</BodySBold>
					</>
				}
			/>
		</>
	);
};

const PrepaidConfirmationDialog = ({
	isOpen,
	hideDialog,
	isError,
	heading,
	button,
	numbers,
	description,
}: {
	isOpen: boolean;
	hideDialog: () => void;
	isError: boolean;
	heading: string;
	button: IConfirmationDialogButton | undefined;
	numbers: string[];
	description: ReactElement;
}) => {
	const classes = useStyles();
	return (
		<CustomConfirmationDialog
			withoutCloseButton={true}
			isOpen={isOpen}
			hideDialog={hideDialog}
			noMargin={true}
			isError={isError}
			width={500}
			padding={'16px 100px'}
			header={
				<>
					<HeadingLBold>{heading}</HeadingLBold>
				</>
			}
			button={button}
		>
			<ConditionalRender
				show={!isError}
				onTrue={() => (
					<>
						<div className={classes.descriptionWrapper}>{description}</div>
						<div className={classes.numbersWrapper}>
							<BodyS useNewLine disableMargin>
								<>
									{translate('prepaid.loyalty.register.loyalty.addon.success.totalNumbers')}{' '}
									{numbers?.length}
								</>
							</BodyS>
						</div>
					</>
				)}
				onFalse={() => (
					<div className={classes.descriptionWrapper}>
						{translate('prepaid.loyalty.register.loyalty.erro.try.again')}
					</div>
				)}
			/>
		</CustomConfirmationDialog>
	);
};
export default PrepaidLoyalty;
